/**
 * CHOOSE LOGIN CONTEXT dialog (web).
 *
 * Shown only when one email genuinely maps to more than one institute/role —
 * an owner who also teaches elsewhere, a parent of two children, a teacher
 * whose own child studies at the same school. A user with a single membership
 * never sees it: the backend answers `selection_required: false` and login
 * continues straight through.
 *
 * The choice is made ONCE, here, and is fixed for the whole session — changing
 * it means signing in again (product decision, no in-session switching). Hence
 * a blocking dialog at login rather than a switcher in the shell.
 *
 * Follows the house conventions: shadcn `@/components/ui/*` + Tailwind
 * utilities + the HSL tokens in global.css. No component CSS file, no inline
 * style objects, and no hardcoded palette classes — the app themes by
 * overriding `--primary` per tenant, so a literal `bg-blue-600` would ignore a
 * customer's brand colour. Role is conveyed by icon and text as well as badge,
 * so it survives greyscale and colour blindness.
 */

import React from 'react';
import {
  AlertCircle,
  Briefcase,
  Building2,
  ChevronRight,
  Globe,
  GraduationCap,
  ShieldCheck,
  Users,
} from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

import type { ContextRole, LoginContext } from './types';
import { orderByLastUsed } from './lastContext';

interface ChooseContextDialogProps {
  open: boolean;
  contexts: LoginContext[];
  loading: boolean;
  onSelect: (membershipUid: string) => void;
  onOpenChange: (open: boolean) => void;
  /** Optional message from a failed selection. */
  error?: string;
  /**
   * The context this browser chose last time for this email, if any. Shown
   * first, badged "Last used" and focused, so the everyday choice is one tap
   * or Enter. It is never auto-selected: the choice stays visible.
   */
  lastUsedUid?: string | null;
}

const isOrganizationRole = (role: ContextRole) => String(role).startsWith('org_');

/** What the role badge says. Organization roles read "Organization" rather
 *  than the raw id ("org_owner"). */
const roleText = (role: ContextRole) => {
  if (!isOrganizationRole(role)) return role;
  return role === 'org_owner' ? 'Organization' : `Organization ${role.slice(4)}`;
};

const roleIcon = (role: ContextRole) => {
  if (isOrganizationRole(role)) return Building2;
  switch (role) {
    case 'platform':
      return Globe;
    case 'owner':
    case 'admin':
      return ShieldCheck;
    case 'faculty':
    case 'staff':
      return Briefcase;
    case 'parent':
      return Users;
    default:
      return GraduationCap;
  }
};

/** Theme-token variants only, so a tenant's brand colour is respected. */
const roleBadge = (role: ContextRole): 'default' | 'secondary' | 'outline' => {
  if (role === 'org_owner') return 'default';
  switch (role) {
    case 'owner':
    case 'admin':
      return 'default';
    case 'faculty':
    case 'staff':
      return 'secondary';
    default:
      return 'outline';
  }
};

const ChooseContextDialog: React.FC<ChooseContextDialogProps> = ({
  open,
  contexts,
  loading,
  onSelect,
  onOpenChange,
  error,
  lastUsedUid = null,
}) => {
  const ordered = orderByLastUsed(contexts, lastUsedUid);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <Users className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-center">Choose how to continue</DialogTitle>
          <DialogDescription className="text-center">
            Your account is linked to more than one institute. Pick one to
            continue — you can change it by signing in again.
          </DialogDescription>
        </DialogHeader>

        {error ? (
          <div className="flex items-center gap-2 rounded-md bg-destructive/10 px-3 py-2">
            <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        ) : null}

        {/* Skeletons only for the FIRST load. Once the rows exist a selection
            keeps them on screen — they simply go disabled — so the dialog does
            not flash back to placeholders on the way out. */}
        {loading && contexts.length === 0 ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-3 p-3">
                <Skeleton className="h-10 w-10 rounded-md" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3.5 w-2/3" />
                  <Skeleton className="h-3 w-2/5" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="max-h-[340px] space-y-2 overflow-y-auto">
            {ordered.map((ctx) => {
              const Icon = roleIcon(ctx.role);
              const isLastUsed = !!lastUsedUid && ctx.membership_uid === lastUsedUid;

              // No per-row hover colours here on purpose. global.css
              // redirects `hover:bg-accent` to `--surface-hover` — the brand
              // hue at 8%, a light wash — and keeps the text at --foreground,
              // with nested `text-muted-foreground` lifted to foreground/72%.
              // Painting children with `group-hover:text-accent-foreground`
              // (white) on top of that wash is what made this row unreadable.
              return (
                <button
                  key={ctx.membership_uid}
                  type="button"
                  disabled={loading}
                  autoFocus={isLastUsed}
                  onClick={() => onSelect(ctx.membership_uid)}
                  aria-label={`Continue as ${ctx.label}`}
                  className="group flex w-full items-center gap-3 rounded-xl border border-border/50 bg-card p-3 text-left shadow-sm transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-60"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted">
                    <Icon className="h-5 w-5 text-primary" />
                  </span>

                  <span className="min-w-0 flex-1">
                    {/* Server-rendered, e.g. "Parent of Ravi Kumar". */}
                    <span className="block truncate text-sm font-medium text-foreground">
                      {ctx.label}
                    </span>
                    {/* institute_name is server-resolved: the institute's
                        name, else the INSTITUTE'S contact/owner email — never
                        a raw id and never the user's own email, which
                        identifies the user, not the institute. */}
                    <span className="block truncate text-xs text-muted-foreground">
                      {ctx.child_email ??
                        (ctx.organization_id && ctx.institute_id === null
                          ? 'Manage all its institutes'
                          : null) ??
                        ctx.institute_name ??
                        (ctx.institute_id !== null ? '' : 'Across all institutes')}
                    </span>
                  </span>

                  {/* The badge keeps its own variant colours: `default` is a
                      solid --primary fill and `outline`/`secondary` carry dark
                      text, all of which read fine on the light hover wash. */}
                  {isLastUsed ? (
                    <Badge variant="outline" className="shrink-0">
                      Last used
                    </Badge>
                  ) : null}
                  <Badge variant={roleBadge(ctx.role)} className="shrink-0 capitalize">
                    {roleText(ctx.role)}
                  </Badge>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ChooseContextDialog;
